/**
 * Typing Indicator Component
 * Displays when users are typing in a channel or DM thread
 */

import { memo } from 'react';
import { useAppStore, type TypingUser } from '@/store/app.store';

interface TypingIndicatorProps {
  conversationId: string | null;
}

// Stable empty array to prevent infinite re-renders
const EMPTY_ARRAY: TypingUser[] = [];

const TypingIndicator = memo(({ conversationId }: TypingIndicatorProps) => {
  const typingUsers = useAppStore((state) => {
    if (!conversationId) return EMPTY_ARRAY;
    return state.typingUsers[conversationId] || EMPTY_ARRAY;
  });

  // Don't render if no one is typing
  if (!typingUsers || typingUsers.length === 0) {
    return null;
  }

  // Format text based on number of users
  const getTypingText = (users: TypingUser[]): string => {
    if (users.length === 1) {
      const verb = users[0].isBot ? 'мисли...' : 'пише...';
      return `${users[0].displayName} ${verb}`;
    }

    if (users.length === 2) {
      return `${users[0].displayName} и ${users[1].displayName} пишат...`;
    }

    // 3+ users: show first 2 + count
    const remaining = users.length - 2;
    const othersText = remaining === 1 ? 'друг' : 'други';
    return `${users[0].displayName}, ${users[1].displayName} и ${remaining} ${othersText} пишат...`;
  };

  return (
    <div
      className="px-6 py-2 text-sm text-gray-500 animate-fade-in"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex items-center space-x-2 max-w-[960px] mx-auto w-full">
        <span>{getTypingText(typingUsers)}</span>

        {/* Animated dots */}
        <div className="flex space-x-1">
          <span
            className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce-dot"
            style={{ animationDelay: '0s' }}
          />
          <span
            className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce-dot"
            style={{ animationDelay: '0.2s' }}
          />
          <span
            className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce-dot"
            style={{ animationDelay: '0.4s' }}
          />
        </div>
      </div>
    </div>
  );
});

TypingIndicator.displayName = 'TypingIndicator';

export default TypingIndicator;
